import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  InputAdornment,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Send as SendIcon,
} from '@mui/icons-material';

import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { DatePicker, StaticDatePicker, TimePicker } from '@mui/x-date-pickers';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Badge } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getPendingShiftCount, notifyPendingShifts } from '../services/shift';
import { format, addDays, parseISO, isSameDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../config';
import { getAdjustedDate } from '../utils/dateUtils';
import { useSystemSettings } from '../contexts/SystemSettingsContext';

const API_BASE_URL = config.API_BASE_URL;

interface Shift {
  id: number;
  user_id: number;
  date: string;
  start_time: string;
  end_time: string;
  break_start?: string;
  break_end?: string;
  status: 'scheduled' | 'working' | 'completed' | 'cancelled';
  notes?: string;
}

interface Staff {
  id: number;
  name: string;
  role: string;
  area?: string;
  is_active: boolean;
}

interface ShiftFormData {
  id?: string;
  user_id: string;
  date: Date;
  start_time: Date | null;
  end_time: Date | null;
  break_start: Date | null;
  break_end: Date | null;
  status: string;
  notes: string;
}

const initialShiftForm: ShiftFormData = {
  user_id: '',
  date: new Date(),
  start_time: null,
  end_time: null,
  break_start: null,
  break_end: null,
  status: 'scheduled',
  notes: '',
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'scheduled': return 'info';
    case 'working': return 'warning';
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'scheduled': return '予定';
    case 'working': return '勤務中';
    case 'completed': return '完了';
    case 'cancelled': return 'キャンセル';
    default: return status;
  }
};

const getDynamicStatus = (shift: Shift): Shift['status'] => {
  if (shift.status === 'cancelled') {
    return 'cancelled';
  }

  const now = new Date();
  const shiftDate = new Date(shift.date);
  
  const [startHour, startMinute] = shift.start_time.split(':').map(Number);
  const shiftStartDateTime = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate(), startHour, startMinute);

  const [endHour, endMinute] = shift.end_time.split(':').map(Number);
  let shiftEndDateTime = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate(), endHour, endMinute);

  if (shiftEndDateTime < shiftStartDateTime) {
    shiftEndDateTime.setDate(shiftEndDateTime.getDate() + 1);
  }

  if (now >= shiftStartDateTime && now < shiftEndDateTime) {
    return 'working';
  }
  
  if (now >= shiftEndDateTime) {
    return 'completed';
  }

  return 'scheduled';
};


const Shifts: React.FC = () => {
  const { businessHoursSimple } = useSystemSettings();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000); // 1分ごとに現在時刻を更新
    return () => clearInterval(timer);
  }, []);

  const [loading, setLoading] = useState<boolean>(true);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<Staff[]>([]);
  const [periodFilter, setPeriodFilter] = useState<'day' | 'week' | 'month'>('week');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [openShiftForm, setOpenShiftForm] = useState<boolean>(false);
  const [shiftForm, setShiftForm] = useState<ShiftFormData>(initialShiftForm);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalStaff, setTotalStaff] = useState<number>(0);
  const [openShiftDetail, setOpenShiftDetail] = useState<boolean>(false);
  const [shiftsForDay, setShiftsForDay] = useState<Shift[]>([]);
  // 未通知シフトのまとめ送信用
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [notifying, setNotifying] = useState<boolean>(false);

  const shiftCountsByDate = useMemo(() => {
    return shifts.reduce((acc, shift) => {
      const date = format(parseISO(shift.date), 'yyyy-MM-dd');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });
  }, [shifts]);

  const pagedStaff = useMemo(() => {
    const startIndex = (page - 1) * 10;
    return filteredStaff.slice(startIndex, startIndex + 10);
  }, [filteredStaff, page]);

  const dateRange = useMemo(() => {
    if (periodFilter === 'day') return [selectedDate];
    if (periodFilter === 'week') return Array.from({ length: 7 }, (_, i) => addDays(selectedDate, i));
    return [];
  }, [selectedDate, periodFilter]);


  const loadShifts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('認証情報がありません。');
        setLoading(false);
        return;
      }

      let startDateStr, endDateStr;
      if (periodFilter === 'day') {
        startDateStr = format(selectedDate, 'yyyy-MM-dd');
        endDateStr = startDateStr;
      } else if (periodFilter === 'week') {
        startDateStr = format(selectedDate, 'yyyy-MM-dd');
        endDateStr = format(addDays(selectedDate, 6), 'yyyy-MM-dd');
      } else if (periodFilter === 'month') {
        const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        startDateStr = format(startOfMonth, 'yyyy-MM-dd');
        endDateStr = format(endOfMonth, 'yyyy-MM-dd');
      } else {
        setShifts([]);
        setLoading(false);
        return;
      }

      // 日/週表示では、表示中スタッフ(pagedStaff)の分だけ取得する。
      // これによりページ送り・検索でページが変わっても、表示中スタッフのシフトを確実に取得できる。
      // 月表示は件数集計のため全件取得する（user_ids を付けない）。
      const params: any = { start_date: startDateStr, end_date: endDateStr, per_page: 9999 };
      if (periodFilter !== 'month' && pagedStaff.length > 0) {
        params.user_ids = pagedStaff.map(s => s.id).join(',');
      }

      const response = await axios.get(`${API_BASE_URL}/api/shifts`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && Array.isArray(response.data.shifts)) {
        setShifts(response.data.shifts);
      } else {
        setShifts([]);
      }
    } catch (error) {
      toast.error('シフトデータの取得に失敗しました');
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, periodFilter, pagedStaff]);

  // 未通知シフト件数を取得する
  const fetchPendingCount = useCallback(async () => {
    try {
      const count = await getPendingShiftCount();
      setPendingCount(count);
    } catch (e) {
      // 取得失敗は致命的でないので無視
    }
  }, []);

  const handleCalendarDateClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const relevantShifts = shifts.filter(s => s.date.startsWith(dateStr));
    setShiftsForDay(relevantShifts);
    setSelectedDate(date);
    setOpenShiftDetail(true);
  };
  
  const loadAllStaff = useCallback(async () => {
    try {
      console.log('loadAllStaff: スタッフリストの取得を開始します。');
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('認証情報がありません。');
        console.error('loadAllStaff: トークンが見つかりません。');
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { 
          per_page: 10000000, // Get all users by setting a very large number
          role: 'CAST,DRIVER,MANAGER,STAFF'
        } 
      });

      console.log('loadAllStaff: APIレスポンスを受け取りました。', response);

      if (response.data && Array.isArray(response.data.users)) {
        const allowedRoles = ['CAST', 'DRIVER', 'MANAGER', 'STAFF'];
        let activeStaff = response.data.users
          .filter((user: any) => user.is_active && allowedRoles.includes(user.role.toUpperCase()))
          .map((user: any) => ({
            id: user.id,
            name: [user.last_name, user.first_name].filter(Boolean).join(' ') || user.username,
            role: user.role,
            is_active: user.is_active
          }));

        // ログインユーザーが管理者系でない場合(キャスト・ドライバー・内勤スタッフ)は、
        // 自分自身の行だけを表示する。
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const userData = JSON.parse(userStr);
            const loginRole = String(userData.user?.role || userData.role || '').toUpperCase();
            const loginId = userData.user?.id ?? userData.id;
            const managerRoles = ['ADMIN', 'MANAGER', 'DISPATCHER'];
            if (loginId != null && !managerRoles.includes(loginRole)) {
              activeStaff = activeStaff.filter((s: any) => String(s.id) === String(loginId));
            }
          }
        } catch (e) {
          console.warn('loadAllStaff: ログインユーザー情報の解析に失敗しました。', e);
        }

        console.log('loadAllStaff: 処理後のスタッフデータです。', activeStaff);
        setAllStaff(activeStaff);
        setFilteredStaff(activeStaff); 
        setTotalStaff(activeStaff.length);
      } else {
        console.error('loadAllStaff: レスポンスデータが予期した形式ではありません。', response.data);
      }
    } catch (error) {
      console.error('loadAllStaff: スタッフリストの取得中にエラーが発生しました。', error);
      toast.error('スタッフリストの取得に失敗しました。');
    }
  }, []);

  useEffect(() => {
    loadAllStaff();
  }, [loadAllStaff]);

  useEffect(() => {
    loadShifts();
    fetchPendingCount();
  }, [loadShifts, fetchPendingCount]);

  useEffect(() => {
    const handler = setTimeout(() => {
      let staffToFilter = [...allStaff];

      if (roleFilter !== 'all') {
        staffToFilter = staffToFilter.filter(p => p.role === roleFilter);
      }

      if (nameFilter) {
        staffToFilter = staffToFilter.filter(p => p.name.toLowerCase().includes(nameFilter.toLowerCase()));
      }
      
      setFilteredStaff(staffToFilter);
      setTotalStaff(staffToFilter.length);
      setPage(1); // Reset to first page on filter change
    }, 2000); // 2 second delay

    return () => {
      clearTimeout(handler);
    };
  }, [nameFilter, roleFilter, allStaff]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleSaveShift = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('認証トークンが見つかりません。');
        return;
      }
      const formatTime = (date: Date | null) => (date ? format(date, 'HH:mm:ss') : null);

      const payload = {
        user_id: shiftForm.user_id,
        date: format(shiftForm.date, 'yyyy-MM-dd'),
        start_time: formatTime(shiftForm.start_time),
        end_time: formatTime(shiftForm.end_time),
        break_start: formatTime(shiftForm.break_start),
        break_end: formatTime(shiftForm.break_end),
        status: shiftForm.status,
        notes: shiftForm.notes,
      };

      const method = isEditMode ? 'put' : 'post';
      const url = isEditMode
        ? `${API_BASE_URL}/api/shifts/${shiftForm.id}`
        : `${API_BASE_URL}/api/shifts`;

      const response = await axios({
        method,
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        toast.success(isEditMode ? 'シフトを更新しました。' : 'シフトを作成しました。');
        if (isEditMode) {
            setShifts(shifts.map(s => s.id === response.data.id ? response.data : s));
        } else {
            setShifts([...shifts, response.data]);
        }
        handleCloseShiftForm();
        fetchPendingCount(); // 未通知件数を更新
      }
    } catch (error) {
      console.error('Shift save error:', error);
      toast.error('シフトの保存に失敗しました。');
    }
  };

  const handleDeleteShift = async (id: number) => {
    if (window.confirm('このシフトを削除してもよろしいですか？')) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('認証情報がありません。');
          return;
        }
        await axios.delete(`${API_BASE_URL}/api/shifts/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        toast.success('シフトを削除しました');
        setShifts(shifts.filter(s => s.id !== id));
      } catch (error) {
        toast.error('シフトの削除に失敗しました');
      }
    }
  };

  // 編集中のシフトをリセット（休みに）する。シフトは「存在＝勤務／不在＝休み」で表すため、
  // 対象シフトを削除することで「休み」状態に戻す。
  const handleResetToDayOff = async () => {
    if (!isEditMode || !shiftForm.id) return;
    if (!window.confirm('このシフトを削除して「休み」にしますか？')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('認証情報がありません。');
        return;
      }
      await axios.delete(`${API_BASE_URL}/api/shifts/${shiftForm.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('シフトをリセットしました（休み）');
      setShifts(shifts.filter(s => String(s.id) !== String(shiftForm.id)));
      handleCloseShiftForm();
      fetchPendingCount(); // 未通知件数を更新
    } catch (error) {
      toast.error('シフトのリセットに失敗しました');
    }
  };

  // 未通知のシフトをまとめて管理者へLINE送信する
  const handleNotifyPending = async () => {
    if (pendingCount === 0) {
      toast.info('未通知のシフトはありません');
      return;
    }
    if (!window.confirm(`未通知のシフト${pendingCount}件をまとめて送信しますか？`)) return;
    try {
      setNotifying(true);
      const result = await notifyPendingShifts();
      toast.success(result.message || 'シフトを通知しました');
      await fetchPendingCount();
      await loadShifts();
    } catch (e) {
      toast.error('シフトの通知に失敗しました');
    } finally {
      setNotifying(false);
    }
  };

  const getStaffShift = useCallback((staffId: number, date: Date): Shift | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // user_id / staffId は API 由来で number/string が混在しうるため数値化して比較する。
    // s.date は "2026-09-19" と "2026-09-19T..." のどちらの形式でも先頭10文字で判定する。
    return shifts.find(
      s => Number(s.user_id) === Number(staffId) && String(s.date).slice(0, 10) === dateStr
    );
  }, [shifts]);

  const handleOpenShiftForm = (isEdit = false, shiftData?: Shift) => {
    setIsEditMode(isEdit);
    if (isEdit && shiftData) {
        const shiftDate = parseISO(shiftData.date);
        const createTimeObject = (timeStr: string | undefined): Date | null => {
            if (!timeStr) return null;
            const [hours, minutes] = timeStr.split(':').map(Number);
            const timeObj = new Date(shiftDate);
            timeObj.setHours(hours, minutes, 0, 0);
            return timeObj;
        };
        setShiftForm({
            user_id: shiftData.user_id.toString(),
            date: shiftDate,
            start_time: createTimeObject(shiftData.start_time),
            end_time: createTimeObject(shiftData.end_time),
            break_start: createTimeObject(shiftData.break_start),
            break_end: createTimeObject(shiftData.break_end),
            status: shiftData.status || 'scheduled',
            notes: shiftData.notes || '',
            id: shiftData.id.toString()
        });
    } else {
        setShiftForm({ ...initialShiftForm, date: selectedDate });
    }
    setOpenShiftForm(true);
  };

  const handleCellClick = (staffId: number, date: Date) => {
    const existingShift = getStaffShift(staffId, date);
    if (existingShift) {
        handleOpenShiftForm(true, existingShift);
    } else {
        setIsEditMode(false);
        setShiftForm({ ...initialShiftForm, user_id: staffId.toString(), date: date });
        setOpenShiftForm(true);
    }
  };
  
  const handleCloseShiftForm = () => {
    setOpenShiftForm(false);
    setShiftForm(initialShiftForm);
    setIsEditMode(false);
  };
  
  const handleShiftFormChange = (field: keyof ShiftFormData, value: any) => {
    setShiftForm((prev) => ({ ...prev, [field]: value }));
  };
  
  const ShiftInfo = ({ shift }: { shift: Shift | undefined }) => {
    if (!shift) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    
    const dynamicStatus = getDynamicStatus(shift);

    // レイアウトを維持するため、元の構造をベースに修正
    return (
      <Box>
        <Chip 
          label={getStatusLabel(dynamicStatus)} 
          color={getStatusColor(dynamicStatus)} 
          size="small" 
          sx={{ mb: 0.5 }}
        />
        <Typography variant="body2">
          {shift.start_time?.substring(0, 5)} - {shift.end_time?.substring(0, 5)}
        </Typography>
         {shift.notes && (
          <Tooltip title={shift.notes}>
            <Typography variant="caption" color="textSecondary" noWrap>
              {shift.notes}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <DashboardLayout title="シフト管理">
      <PageHeader title="シフト管理" subtitle="スタッフのシフトスケジュールの管理" />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Container maxWidth="lg" sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <Box sx={{ mt: 4, mb: 4, maxWidth: '100%' }}>
          {/* Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <ToggleButtonGroup value={periodFilter} exclusive onChange={(_, v) => v && setPeriodFilter(v)} size="small">
                  <ToggleButton value="day"><TodayIcon sx={{ mr: 1 }} />当日</ToggleButton>
                  <ToggleButton value="week"><DateRangeIcon sx={{ mr: 1 }} />1週間</ToggleButton>
                  <ToggleButton value="month"><EventIcon sx={{ mr: 1 }} />1ヶ月</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker label="日付選択" value={selectedDate} onChange={handleDateChange} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenShiftForm()}>シフト登録</Button>
                  {/* 未通知のシフトをまとめて送信。未送信があるときは目立たせる。 */}
                  <Badge badgeContent={pendingCount} color="error" overlap="rectangular">
                    <Button
                      variant={pendingCount > 0 ? 'contained' : 'outlined'}
                      color={pendingCount > 0 ? 'error' : 'inherit'}
                      startIcon={<SendIcon />}
                      onClick={handleNotifyPending}
                      disabled={notifying || pendingCount === 0}
                      sx={pendingCount > 0 ? { fontWeight: 'bold', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.6 } } } : undefined}
                    >
                      {notifying ? '送信中...' : `未通知を送信${pendingCount > 0 ? `（${pendingCount}件）` : ''}`}
                    </Button>
                  </Badge>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>役割フィルター</InputLabel>
                  <Select value={roleFilter} label="役割フィルター" onChange={(e) => setRoleFilter(e.target.value)}>
                    <MenuItem value="all">すべて</MenuItem>
                    <MenuItem value="MANAGER">マネージャー</MenuItem>
                    <MenuItem value="STAFF">内勤スタッフ</MenuItem>
                    <MenuItem value="DRIVER">ドライバー</MenuItem>
                    <MenuItem value="CAST">キャスト</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="名前で検索" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)}} />
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              {periodFilter === 'month' ? (
                <Paper sx={{ p: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      openTo="day"
                      value={selectedDate}
                      slots={{
                        day: (props) => {
                          const dateStr = format(props.day, 'yyyy-MM-dd');
                          const shiftCount = shiftCountsByDate[dateStr];
                          return (
                            <Badge
                              key={props.day.toString()}
                              overlap="circular"
                              badgeContent={shiftCount}
                              color="secondary"
                            >
                              <PickersDay {...props} onClick={() => handleCalendarDateClick(props.day)} />
                            </Badge>
                          );
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Paper>
              ) : (
              <>
              <Paper sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                {/*
                  名前列とシフト列を1つのテーブルにまとめ、TableContainer全体で横スクロールさせる。
                  名前列は position:sticky で左端に固定し、スマホでも横幅がはみ出さず
                  スクロールで日付列を確認できるようにする。
                  maxWidth:'100%' を明示し、親(flexアイテムであるmainボックス)の幅を
                  テーブルのminWidthで突き破らないようにする。
                */}
                <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', maxWidth: '100%', overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 150 + dateRange.length * 150 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            bgcolor: 'background.paper',
                            minWidth: 150,
                          }}
                        >
                          名前
                        </TableCell>
                        {dateRange.map((date) => (
                          <TableCell
                            key={date.toString()}
                            align="center"
                            sx={{
                              minWidth: 150,
                              fontWeight: 'bold',
                              bgcolor: isSameDay(date, new Date()) ? 'primary.light' : 'inherit',
                            }}
                          >
                            {format(date, 'MM/dd (E)', { locale: ja })}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedStaff.map((person) => (
                        <TableRow key={person.id}>
                          <TableCell
                            sx={{
                              height: 120,
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              minWidth: 150,
                            }}
                          >
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{person.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{person.role}</Typography>
                          </TableCell>
                          {dateRange.map((date) => {
                            const shift = getStaffShift(person.id, date);
                            return (
                              <TableCell key={`${person.id}-${date.toString()}`} align="center" onClick={() => handleCellClick(person.id, date)} sx={{ minWidth: 150, height: 120, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                                {shift ? <ShiftInfo shift={shift} /> : <Box sx={{ color: 'text.secondary' }}><AddIcon fontSize="small" sx={{ mr: 0.5 }} />登録</Box>}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={Math.ceil(totalStaff / 10)} page={page} onChange={(_, p) => setPage(p)} color="primary" />
              </Box>
            </>
            )}
            </>
          )}
        </Box>
      </Container>
      {/* Shift form dialog */}
      <Dialog open={openShiftForm} onClose={handleCloseShiftForm} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'シフト編集' : 'シフト登録'}</DialogTitle>
        <DialogContent>
            <Grid container spacing={2} sx={{mt: 1}}>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>スタッフ</InputLabel>
                        <Select
                            value={shiftForm.user_id}
                            label="スタッフ"
                            onChange={(e) => handleShiftFormChange('user_id', e.target.value)}
                        >
                            {allStaff.map((person) => (
                                <MenuItem key={person.id} value={person.id.toString()}>
                                    {person.name} ({person.role})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                  <DatePicker
                    label="日付"
                    value={shiftForm.date}
                    onChange={(newValue) => handleShiftFormChange('date', newValue)}
                    disabled={isEditMode}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                 <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                  <TimePicker
                    label="開始時間"
                    value={shiftForm.start_time}
                    onChange={(newValue) => handleShiftFormChange('start_time', newValue)}
                    ampm={false}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                 <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                  <TimePicker
                    label="終了時間"
                    value={shiftForm.end_time}
                    onChange={(newValue) => handleShiftFormChange('end_time', newValue)}
                    ampm={false}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                 <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                  <TimePicker
                    label="休憩開始"
                    value={shiftForm.break_start}
                    onChange={(newValue) => handleShiftFormChange('break_start', newValue)}
                    ampm={false}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                 <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                  <TimePicker
                    label="休憩終了"
                    value={shiftForm.break_end}
                    onChange={(newValue) => handleShiftFormChange('break_end', newValue)}
                    ampm={false}
                  />
                </LocalizationProvider>
              </Grid>
               <Grid item xs={12}>
                <FormControl fullWidth>
                    <TextField label="備考" multiline rows={2} fullWidth value={shiftForm.notes} onChange={(e) => handleShiftFormChange('notes', e.target.value)}/>
                </FormControl>
            </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {/* 編集時のみ「休みにする（シフト削除）」ボタンを左端に表示 */}
          <Box>
            {isEditMode && (
              <Button onClick={handleResetToDayOff} color="error" variant="outlined">
                リセット（休みにする）
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={handleCloseShiftForm} sx={{ mr: 1 }}>キャンセル</Button>
            <Button onClick={handleSaveShift} variant="contained" color="primary">保存</Button>
          </Box>
        </DialogActions>
      </Dialog>
      {/* Shift detail dialog */}
        <Dialog open={openShiftDetail} onClose={() => setOpenShiftDetail(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                シフト詳細 - {format(selectedDate, 'MM月dd日 (E)', { locale: ja })}
                <IconButton onClick={() => { setOpenShiftDetail(false); handleOpenShiftForm(false); }} sx={{float: 'right'}}>
                    <AddIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {shiftsForDay.length > 0 ? (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>スタッフ</TableCell>
                                    <TableCell>勤務時間</TableCell>
                                    <TableCell>休憩時間</TableCell>
                                    <TableCell>ステータス</TableCell>
                                    <TableCell>操作</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shiftsForDay.map(shift => {
                                    const staff = allStaff.find(s => s.id === shift.user_id);
                                    const dynamicStatus = getDynamicStatus(shift);
                                    return (
                                        <TableRow key={shift.id}>
                                            <TableCell>{staff?.name || '不明'}</TableCell>
                                            <TableCell>{shift.start_time?.substring(0, 5)} - {shift.end_time?.substring(0, 5)}</TableCell>
                                            <TableCell>{shift.break_start ? `${shift.break_start.substring(0,5)} - ${shift.break_end?.substring(0,5)}` : '-'}</TableCell>
                                            <TableCell><Chip label={getStatusLabel(dynamicStatus)} color={getStatusColor(dynamicStatus)} size="small"/></TableCell>
                                            <TableCell>
                                                <Tooltip title="編集"><IconButton size="small" onClick={() => { setOpenShiftDetail(false); handleOpenShiftForm(true, shift);}}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="削除"><IconButton size="small" onClick={() => handleDeleteShift(shift.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>この日のシフトはありません。</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenShiftDetail(false)}>閉じる</Button>
            </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </DashboardLayout>
  );
};

export default Shifts; 